// ============================================================================
// PACK MANAGER - Platform Admin
// 
// TENANT-SCOPED DESIGN:
// 1. Select Tenant first
// 2. See specialties GROUPED BY CATEGORY (collapsible)
// 3. Toggle individual subspecialties on/off for tenant
// 4. When specialty is enabled, can upload/manage packs
// 
// Every pack belongs to exactly one tenant.
// UI shows explicit tenant ownership at all times.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { GiLungs } from 'react-icons/gi';
import {
  Building, Shield, Plus, Trash2, Check, X, Loader2,
  AlertCircle, AlertTriangle, ChevronRight, FileJson, CheckCircle,
  Eye, Clock, Archive, ArchiveRestore, Upload, Star, Activity, Brain, Zap, Hand, Sparkles,
  ToggleLeft, ToggleRight, Package, ChevronDown, ChevronUp,
  Heart, Stethoscope, Pill, Baby, Bone, Droplet, Scissors,
  Ear, SunMedium, FlaskConical, Radiation, Microscope, Syringe,
  HeartPulse, BrainCircuit, PersonStanding, Pencil
} from 'lucide-react';
import {
  getTenants, getSpecialties, getPacks, getPackByDocId,
  savePackVersion, updatePackStatus, updatePackDoc,
  deletePack, validatePack, saveSpecialty
} from '../core/index.js';
import { getSpecialtiesByGroup, getCatalogEntry } from '../core/specialtyCatalog.js';
import PackVersionHistoryDrawer from './PackVersionHistoryDrawer.jsx';

// ============================================================================
// SPECIALTY CATEGORIES WITH SUBSPECIALTIES
// Grouped structure for better organization
// ============================================================================

// SPECIALTY CATALOG V2 (display-layer): the hardcoded catalog now lives in
// src/core/specialtyCatalog.js. Derive render views — each V2 group is shaped like
// the legacy "category" so CategoryAccordion renders unchanged. specialtyId is the
// unchanged Firestore key; category is display-only. Entries expose name(=label),
// description, status, and tenantConfigurable (preventive_medicine is locked).
const SPECIALTY_GROUP_VIEWS = getSpecialtiesByGroup().map((g) => ({
  id: g.id,
  name: g.label,
  icon: g.icon,
  specialties: g.specialties.map((s) => ({
    id: s.id,
    name: s.label,
    description: s.description,
    status: s.status,
    tenantConfigurable: s.visibility.tenantConfigurable,
  })),
}));

// Icon mapping - using available Lucide icons + react-icons
const ICON_MAP = {
  Brain, Zap, Hand, Activity, Sparkles, Eye, Shield, AlertCircle, AlertTriangle,
  Heart, Stethoscope, Pill, Baby, Bone, Droplet, Scissors,
  Ear, SunMedium, FlaskConical, Radiation, Microscope, Syringe,
  HeartPulse, BrainCircuit, PersonStanding,

  // alias for respiratory (GiLungs from react-icons/gi)
  Lung: GiLungs,
};

// ============================================================================
// MODAL COMPONENT
// ============================================================================

const Modal = ({ isOpen, onClose, title, size = 'md', children }) => {
  if (!isOpen) return null;
  const sizes = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl w-full ${sizes[size]} max-h-[90vh] overflow-hidden flex flex-col`}>
        <div className="flex items-center justify-between p-6 border-b border-slate-700/50">
          <h2 className="text-xl font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
};

// V2 group → item noun (singular / plural). Drives the group-header subtitle so
// it no longer always says "subspecialties" (e.g. "1 source layer", "2 pathways",
// "7 service packs"). UI copy only.
const GROUP_ITEM_NOUN = {
  clinical_referral:   ['specialty', 'specialties'],
  surgical_procedural: ['specialty', 'specialties'],
  diagnostic_service:  ['service pack', 'service packs'],
  high_acuity_pathway: ['pathway', 'pathways'],
  optional_track:      ['optional track', 'optional tracks'],
  hidden_source:       ['source layer', 'source layers'],
};
const groupItemLabel = (groupId, count) => {
  const [singular, plural] = GROUP_ITEM_NOUN[groupId] || ['specialty', 'specialties'];
  return count === 1 ? singular : plural;
};

// ============================================================================
// CATEGORY ACCORDION COMPONENT
// ============================================================================

const CategoryAccordion = ({ 
  category, 
  enabledSpecialties, 
  selectedTenant, 
  onToggleSpecialty, 
  togglingSpecialty,
  isExpanded,
  onToggleExpand 
}) => {
  const CategoryIcon = ICON_MAP[category.icon] || Shield;
  
  // Count enabled specialties in this category
  const enabledCount = category.specialties.filter(s => enabledSpecialties[s.id]).length;
  const totalCount = category.specialties.length;
  
  return (
    <div className="border border-slate-700/50 rounded-xl overflow-hidden">
      {/* Category Header */}
      <button
        onClick={onToggleExpand}
        className={`w-full flex items-center justify-between p-4 transition-colors ${
          isExpanded ? 'bg-slate-800/50' : 'bg-slate-800/30 hover:bg-slate-800/40'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            enabledCount > 0 ? 'bg-emerald-500/20' : 'bg-slate-700/50'
          }`}>
            <CategoryIcon className={`w-5 h-5 ${enabledCount > 0 ? 'text-emerald-400' : 'text-slate-500'}`} />
          </div>
          <div className="text-left">
            <p className="font-medium text-white">{category.name}</p>
            <p className="text-xs text-slate-500">{totalCount} {groupItemLabel(category.id, totalCount)}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Enabled count badge */}
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
            enabledCount > 0 
              ? 'bg-emerald-500/20 text-emerald-400' 
              : 'bg-slate-700/50 text-slate-500'
          }`}>
            {enabledCount}/{totalCount} enabled
          </span>
          
          {/* Expand/collapse icon */}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </button>
      
      {/* Subspecialties List (collapsible) */}
      {isExpanded && (
        <div className="border-t border-slate-700/50 bg-slate-900/50 p-4">
          <div className="space-y-2">
            {category.specialties.map((spec) => {
              const isEnabled = !!enabledSpecialties[spec.id];
              const isToggling = togglingSpecialty === spec.id;
              
              return (
                <div
                  key={spec.id}
                  className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                    isEnabled 
                      ? 'bg-emerald-500/10 border border-emerald-500/30' 
                      : 'bg-slate-800/30 border border-slate-700/30 hover:border-slate-600/50'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium ${isEnabled ? 'text-white' : 'text-slate-400'}`}>
                      {spec.name}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{spec.description}</p>
                  </div>
                  
                  <div className="flex items-center gap-3 ml-4">
                    {/* V2 status metadata pill (non-default statuses only) */}
                    {spec.status && spec.status !== 'default_visible' && (
                      <span className="hidden md:inline-flex px-2 py-0.5 rounded-full bg-slate-700/40 text-slate-400 text-[10px] uppercase tracking-wide">
                        {spec.status.replace(/_/g, ' ')}
                      </span>
                    )}
                    {/* Status indicator */}
                    {isEnabled ? (
                      <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs">
                        <CheckCircle className="w-3 h-3" />
                        Enabled
                      </span>
                    ) : spec.tenantConfigurable === false ? (
                      <span className="hidden sm:inline-flex px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-500 text-xs">
                        Locked
                      </span>
                    ) : (
                      <span className="hidden sm:inline-flex px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-500 text-xs">
                        Not enabled
                      </span>
                    )}

                    {/* Toggle button (locked when not tenant-configurable, e.g. deferred specialties) */}
                    <button
                      onClick={() => onToggleSpecialty(spec)}
                      disabled={isToggling || spec.tenantConfigurable === false}
                      className={`p-1.5 rounded-lg transition-colors ${
                        spec.tenantConfigurable === false
                          ? 'text-slate-600 cursor-not-allowed opacity-50'
                          : isEnabled
                          ? 'text-emerald-400 hover:bg-emerald-500/20'
                          : 'text-slate-500 hover:bg-slate-700/50 hover:text-slate-300'
                      }`}
                      title={spec.tenantConfigurable === false ? 'Deferred — not available for enablement' : isEnabled ? `Disable for ${selectedTenant?.name}` : `Enable for ${selectedTenant?.name}`}
                    >
                      {isToggling ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : isEnabled ? (
                        <ToggleRight className="w-5 h-5" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// PACK MANAGER VIEW
// ============================================================================

const PackManagerView = () => {
  // State
  const [tenants, setTenants] = useState({});
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [enabledSpecialties, setEnabledSpecialties] = useState({});
  const [selectedSpecialtyId, setSelectedSpecialtyId] = useState(null);
  const [packs, setPacks] = useState({});
  // Read-only Version History drawer target: { specialtyId, packId, name } | null
  const [historyPack, setHistoryPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [loadingPacks, setLoadingPacks] = useState(false);
  const [togglingSpecialty, setTogglingSpecialty] = useState(null);
  
  // Accordion state - track which categories are expanded
  const [expandedCategories, setExpandedCategories] = useState({});

  // Modals
  const [showUpload, setShowUpload] = useState(false);

  // Upload / edit state
  const [jsonInput, setJsonInput] = useState('');
  const [validation, setValidation] = useState(null);
  const [parsedPack, setParsedPack] = useState(null);
  const [saving, setSaving] = useState(false);

  // Edit-mode state
  const [editMode, setEditMode] = useState(false);
  const [editingPackDocId, setEditingPackDocId] = useState(null);
  const [editingPackName, setEditingPackName] = useState('');
  const [loadingEditPack, setLoadingEditPack] = useState(false);

  // Redirect flag visual editor state
  const [addingIntraRow, setAddingIntraRow] = useState(false);
  const [addingCrossRow, setAddingCrossRow] = useState(false);
  const [editingIntraIdx, setEditingIntraIdx] = useState(null);   // index being edited, or null
  const [editingCrossIdx, setEditingCrossIdx] = useState(null);
  const [intraRowDraft, setIntraRowDraft] = useState({});
  const [crossRowDraft, setCrossRowDraft] = useState({});

  // Selected tenant object
  const selectedTenant = selectedTenantId ? tenants[selectedTenantId] : null;

  // ============================================================================
  // LOAD DATA
  // ============================================================================

  // Load tenants on mount (do NOT auto-select - show "Select a tenant..." prompt first)
  useEffect(() => {
    const loadTenants = async () => {
      try {
        const data = await getTenants();
        setTenants(data);
        // Do NOT auto-select first tenant - let user choose
        setLoading(false);
      } catch (err) {
        console.error('Error loading tenants:', err);
        setLoading(false);
      }
    };
    loadTenants();
  }, []);

  // Load specialties when tenant changes
  useEffect(() => {
    if (!selectedTenantId) {
      setEnabledSpecialties({});
      setSelectedSpecialtyId(null);
      setPacks({});
      return;
    }

    const loadSpecialties = async () => {
      setLoadingSpecialties(true);
      try {
        const data = await getSpecialties(selectedTenantId);
        setEnabledSpecialties(data || {});
        
        // Do NOT auto-select — require user to pick a specialty
        const enabledIds = Object.keys(data || {});
        if (enabledIds.length === 0) {
          setSelectedSpecialtyId(null);
          setPacks({});
        }
      } catch (err) {
        console.error('Error loading specialties:', err);
        setEnabledSpecialties({});
      } finally {
        setLoadingSpecialties(false);
      }
    };
    loadSpecialties();
  }, [selectedTenantId]);

  // Load packs when specialty changes
  useEffect(() => {
    if (!selectedTenantId || !selectedSpecialtyId) {
      setPacks({});
      return;
    }

    const loadPacks = async () => {
      setLoadingPacks(true);
      try {
        // Pack Manager needs to see ALL packs, not just active ones
        const data = await getPacks(selectedTenantId, selectedSpecialtyId, { includeInactive: true });
        setPacks(data || {});
      } catch (err) {
        console.error('Error loading packs:', err);
        if (err.code === 'permission-denied') {
          alert('Permission denied: Unable to load packs for this specialty.');
        }
        setPacks({});
      } finally {
        setLoadingPacks(false);
      }
    };
    loadPacks();
  }, [selectedTenantId, selectedSpecialtyId]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const toggleCategoryExpand = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleToggleSpecialty = async (spec) => {
    if (!selectedTenantId) return;
    
    const isEnabled = !!enabledSpecialties[spec.id];
    setTogglingSpecialty(spec.id);

    try {
      if (isEnabled) {
        // Disable: set status inactive (preserve document + nested packs)
        await saveSpecialty(selectedTenantId, {
          specialtyId: spec.id,
          name: spec.name,
          description: spec.description,
          status: 'inactive',
        });

        // Remove from local active map
        const newEnabled = { ...enabledSpecialties };
        delete newEnabled[spec.id];
        setEnabledSpecialties(newEnabled);

        // If this was selected, clear selection
        if (selectedSpecialtyId === spec.id) {
          const remaining = Object.keys(newEnabled);
          setSelectedSpecialtyId(remaining.length > 0 ? remaining[0] : null);
        }
      } else {
        // Enable: Create specialty for tenant.
        // Icon comes from the V2 catalog entry (Firestore shape unchanged: name/description/icon snapshot).
        const categoryIcon = getCatalogEntry(spec.id)?.icon || 'Shield';

        await saveSpecialty(selectedTenantId, {
          specialtyId: spec.id,
          name: spec.name,
          description: spec.description,
          icon: categoryIcon,
          status: 'active',
          sortOrder: 0,
        });
        
        // Reload specialties
        const data = await getSpecialties(selectedTenantId);
        setEnabledSpecialties(data || {});
        
        // Select the newly enabled specialty
        setSelectedSpecialtyId(spec.id);
      }
    } catch (err) {
      console.error('Error toggling specialty:', err);
      alert('Failed to toggle specialty: ' + safeUserError(err));
    } finally {
      setTogglingSpecialty(null);
    }
  };

  // Pack actions (BLOCK 8: Enhanced validation)
  const handleValidate = () => {
    try {
      const pack = JSON.parse(jsonInput);
      const result = validatePack(pack);
      setValidation(result);
      if (result.valid && result.normalized) {
        setParsedPack(result.normalized); // Use normalized pack with safe defaults
      } else {
        setParsedPack(null);
      }
    } catch (e) {
      setValidation({ valid: false, errors: [`Invalid JSON: ${e.message}`], warnings: [] });
      setParsedPack(null);
    }
  };

  // ── Shared modal helpers ──────────────────────────────────────────────────

  /** Extract the canonical pack JSON from a normalized pack object for the editor textarea.
   *  Prefers pack.pack (the raw Firestore-stored JSON) so ALL original fields are preserved
   *  (notYetSteps, escalationOptions, attachmentRequirements, scope, etc.).
   *  Falls back to assembling from normalized fields when raw content is unavailable.
   */
  const buildPackJsonForEditor = (pack) => {
    // Raw pack content stored in Firestore — use it directly to preserve every original field.
    if (pack.pack && typeof pack.pack === 'object' && Object.keys(pack.pack).length > 0) {
      return pack.pack;
    }
    // Fallback: assemble from normalized fields (create-mode or legacy shape).
    const out = {};
    if (pack.metadata)                              out.metadata = pack.metadata;
    if (pack.intraSpecialtyRedirectFlags?.length)   out.intraSpecialtyRedirectFlags = pack.intraSpecialtyRedirectFlags;
    if (pack.crossSpecialtyRedirectFlags?.length)   out.crossSpecialtyRedirectFlags = pack.crossSpecialtyRedirectFlags;
    if (pack.fields?.length)                        out.fields = pack.fields;
    if (pack.redFlags?.length)                      out.redFlags = pack.redFlags;
    if (pack.notYetNextSteps?.length)               out.notYetNextSteps = pack.notYetNextSteps;
    if (pack.reasonForEscalationOptions?.length)    out.reasonForEscalationOptions = pack.reasonForEscalationOptions;
    if (pack.patientMessageTemplates)               out.patientMessageTemplates = pack.patientMessageTemplates;
    return out;
  };

  // ── Redirect flag visual editor helpers ──────────────────────────────────

  /** Apply a redirect change to parsedPack and regenerate jsonInput to keep them in sync. */
  const applyRedirectChange = (newIntra, newCross) => {
    const updated = {
      ...parsedPack,
      intraSpecialtyRedirectFlags: newIntra,
      crossSpecialtyRedirectFlags: newCross,
    };
    setParsedPack(updated);
    setJsonInput(JSON.stringify(buildPackJsonForEditor(updated), null, 2));
  };

  const EMPTY_REDIRECT_ROW = { id: '', label: '', description: '', redirectReason: '', targetId: '', targetLabel: '' };

  const handleCloseModal = () => {
    setShowUpload(false);
    setEditMode(false);
    setEditingPackDocId(null);
    setEditingPackName('');
    setJsonInput('');
    setValidation(null);
    setParsedPack(null);
  };

  // ── Create mode ───────────────────────────────────────────────────────────

  const handleUploadSave = async () => {
    if (!parsedPack || !selectedTenantId || !selectedSpecialtyId) return;

    setSaving(true);
    try {
      await savePackVersion(selectedTenantId, selectedSpecialtyId, {
        packId: parsedPack.metadata.id,
        versionId: parsedPack.metadata.version,
        name: parsedPack.metadata.name,
        status: 'active',
        pack: parsedPack,
      });

      const data = await getPacks(selectedTenantId, selectedSpecialtyId, { includeInactive: true });
      setPacks(data);
      handleCloseModal();
    } catch (err) {
      console.error('Error saving pack:', err);
      alert('Failed to save pack: ' + safeUserError(err));
    } finally {
      setSaving(false);
    }
  };

  // ── Edit mode ─────────────────────────────────────────────────────────────

  /** Open the modal pre-loaded with an existing pack's JSON (edit-in-place). */
  const handleOpenEditPack = async (pack) => {
    setEditMode(true);
    setEditingPackDocId(pack.packDocId);
    setEditingPackName(pack.name || pack.packId);
    setShowUpload(true);
    setJsonInput('');
    setValidation(null);
    setParsedPack(null);
    setLoadingEditPack(true);

    try {
      const loaded = await getPackByDocId(selectedTenantId, selectedSpecialtyId, pack.packDocId);
      if (!loaded) throw new Error('Pack not found in Firestore');

      const packJson = buildPackJsonForEditor(loaded);
      const jsonStr = JSON.stringify(packJson, null, 2);
      setJsonInput(jsonStr);

      // Auto-validate on load so the Save button is immediately enabled
      const result = validatePack(packJson);
      setValidation(result);
      if (result.valid && result.normalized) setParsedPack(result.normalized);
    } catch (err) {
      console.error('Error loading pack for editing:', err);
      setValidation({ valid: false, errors: [`Failed to load pack: ${safeUserError(err)}`], warnings: [] });
    } finally {
      setLoadingEditPack(false);
    }
  };

  /** Save edits back to the SAME Firestore document. */
  const handleEditSave = async () => {
    if (!parsedPack || !selectedTenantId || !selectedSpecialtyId || !editingPackDocId) return;

    // Guard: metadata.id must not differ from the original packId
    const originalPackId = editingPackDocId.split('__')[0];
    if (parsedPack.metadata?.id && parsedPack.metadata.id !== originalPackId) {
      setValidation(prev => ({
        ...prev,
        valid: false,
        errors: ['Pack ID cannot be changed when editing an existing pack.'],
      }));
      return;
    }

    setSaving(true);
    try {
      await updatePackDoc(selectedTenantId, selectedSpecialtyId, editingPackDocId, parsedPack);
      const data = await getPacks(selectedTenantId, selectedSpecialtyId, { includeInactive: true });
      setPacks(data);
      handleCloseModal();
    } catch (err) {
      console.error('Error saving pack edits:', err);
      alert('Failed to save changes: ' + safeUserError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSetActive = async (packId, versionId) => {
    try {
      await updatePackStatus(selectedTenantId, selectedSpecialtyId, packId, versionId, 'active');
      const data = await getPacks(selectedTenantId, selectedSpecialtyId, { includeInactive: true });
      setPacks(data);
    } catch (err) {
      console.error('Error activating pack:', err);
      alert('Failed to activate pack: ' + safeUserError(err));
    }
  };

  const handleDeactivate = async (packId, versionId) => {
    try {
      await updatePackStatus(selectedTenantId, selectedSpecialtyId, packId, versionId, 'draft');
      const data = await getPacks(selectedTenantId, selectedSpecialtyId, { includeInactive: true });
      setPacks(data);
    } catch (err) {
      console.error('Error deactivating pack:', err);
      alert('Failed to deactivate pack: ' + safeUserError(err));
    }
  };

  const handleArchivePack = async (packId, versionId) => {
    try {
      await updatePackStatus(selectedTenantId, selectedSpecialtyId, packId, versionId, 'archived');
      const data = await getPacks(selectedTenantId, selectedSpecialtyId, { includeInactive: true });
      setPacks(data);
    } catch (err) {
      console.error('Error archiving pack:', err);
      alert('Failed to archive pack: ' + safeUserError(err));
    }
  };

  const handleUnarchivePack = async (packId, versionId) => {
    try {
      // Unarchive sets status back to 'draft' (not 'active') so admin can review before activating
      await updatePackStatus(selectedTenantId, selectedSpecialtyId, packId, versionId, 'draft');
      const data = await getPacks(selectedTenantId, selectedSpecialtyId, { includeInactive: true });
      setPacks(data);
    } catch (err) {
      console.error('Error unarchiving pack:', err);
      alert('Failed to unarchive pack: ' + safeUserError(err));
    }
  };

  const handleDeletePack = async (packId, versionId) => {
    if (!confirm(`Delete this pack from ${selectedTenant?.name}? This cannot be undone.`)) return;
    
    try {
      await deletePack(selectedTenantId, selectedSpecialtyId, packId, versionId);
      const data = await getPacks(selectedTenantId, selectedSpecialtyId, { includeInactive: true });
      setPacks(data);
    } catch (err) {
      console.error('Error deleting pack:', err);
      alert('Failed to delete pack: ' + safeUserError(err));
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const tenantList = Object.entries(tenants);
  const packList = Object.entries(packs);

  // Count total enabled specialties
  const totalEnabledCount = Object.keys(enabledSpecialties).length;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 mb-4">
          <Package className="w-4 h-4 text-amber-400" />
          <span className="text-xs font-medium text-amber-400 uppercase">Pack Manager</span>
        </div>
        <h1 className="text-2xl font-semibold text-white mb-2">Manage Packs by Tenant</h1>
        <p className="text-slate-400">Enable specialties and upload packs for each tenant</p>
      </div>

      {/* Step 1: Tenant Selection */}
      <div className="mb-8">
        <label className="block text-sm font-medium text-slate-300 mb-3">
          Step 1: Select Tenant
        </label>
        <div className="relative inline-block w-full max-w-xs">
          <select
            value={selectedTenantId || ''}
            onChange={(e) => setSelectedTenantId(e.target.value || null)}
            className="w-full appearance-none bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 pr-10 text-white focus:outline-none focus:border-violet-500/50 cursor-pointer"
          >
            <option value="">Select a tenant...</option>
            {tenantList.map(([id, tenant]) => (
              <option key={id} value={id}>{tenant.name}</option>
            ))}
          </select>
          <Building className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 pointer-events-none" />
        </div>
      </div>

      {!selectedTenantId ? (
        <div className="text-center py-12 text-slate-500">
          Select a tenant to manage their specialties and packs
        </div>
      ) : (
        <>
          {/* Tenant Context Banner */}
          <div className="mb-6 p-4 bg-violet-500/10 border border-violet-500/30 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building className="w-5 h-5 text-violet-400" />
              <span className="text-violet-300">
                Managing packs for <strong className="text-white">{selectedTenant?.name}</strong>
              </span>
            </div>
            <span className="text-sm text-violet-400">
              {totalEnabledCount} specialties enabled
            </span>
          </div>

          {/* Step 2: Specialty Categories (Accordion) */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-slate-300 mb-3">
              Step 2: Enable Specialties for {selectedTenant?.name}
            </label>
            
            {loadingSpecialties ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
              </div>
            ) : (
              <div className="space-y-2">
                {SPECIALTY_GROUP_VIEWS.map((category) => (
                  <CategoryAccordion
                    key={category.id}
                    category={category}
                    enabledSpecialties={enabledSpecialties}
                    selectedTenant={selectedTenant}
                    onToggleSpecialty={handleToggleSpecialty}
                    togglingSpecialty={togglingSpecialty}
                    isExpanded={expandedCategories[category.id] || false}
                    onToggleExpand={() => toggleCategoryExpand(category.id)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Step 3: Pack Management */}
          {totalEnabledCount > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-3">
                Step 3: Manage Packs
              </label>
              
              {/* Enabled Specialty Dropdown */}
              <div className="mb-4">
                <div className="relative inline-block w-full max-w-xs">
                  <select
                    value={selectedSpecialtyId || ''}
                    onChange={(e) => setSelectedSpecialtyId(e.target.value || null)}
                    className="w-full appearance-none bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 pr-10 text-white focus:outline-none focus:border-emerald-500/50 cursor-pointer"
                  >
                    <option value="">Select specialty...</option>
                    {Object.entries(enabledSpecialties).map(([id, spec]) => (
                      <option key={id} value={id}>{spec.name}</option>
                    ))}
                  </select>
                  <Shield className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 pointer-events-none" />
                </div>
              </div>

              {/* Packs List */}
              {selectedSpecialtyId && (
                <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl overflow-hidden">
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 border-b border-slate-700/50">
                    <div className="flex items-center gap-2">
                      <FileJson className="w-5 h-5 text-emerald-400" />
                      <span className="font-medium text-white">
                        Packs for {enabledSpecialties[selectedSpecialtyId]?.name}
                      </span>
                      <span className="text-xs text-slate-500">
                        ({selectedTenant?.name})
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setEditMode(false);
                        setEditingPackDocId(null);
                        setEditingPackName('');
                        setShowUpload(true);
                        setJsonInput('');
                        setValidation(null);
                        setParsedPack(null);
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 rounded-lg text-white text-sm font-medium"
                    >
                      <Upload className="w-4 h-4" />
                      Upload Pack
                    </button>
                  </div>

                  {/* Pack List */}
                  {loadingPacks ? (
                    <div className="p-8 text-center">
                      <Loader2 className="w-6 h-6 animate-spin text-emerald-400 mx-auto" />
                    </div>
                  ) : packList.length === 0 ? (
                    <div className="p-12 text-center">
                      <div className="w-16 h-16 rounded-2xl bg-slate-800/50 flex items-center justify-center mx-auto mb-4">
                        <Package className="w-8 h-8 text-slate-600" />
                      </div>
                      <p className="text-slate-400 mb-2">No packs yet for this specialty</p>
                      <p className="text-sm text-slate-500">
                        Upload the first pack for {enabledSpecialties[selectedSpecialtyId]?.name} in {selectedTenant?.name}
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-700/50">
                      {packList.map(([key, pack]) => {
                        const isActive = pack.status === 'active';
                        
                        return (
                          <div key={key} className="p-4 hover:bg-slate-800/30">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  isActive ? 'bg-emerald-500/20' : 'bg-slate-700/50'
                                }`}>
                                  <FileJson className={`w-5 h-5 ${isActive ? 'text-emerald-400' : 'text-slate-500'}`} />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-white">{pack.name || pack.packId}</p>
                                    {isActive && (
                                      <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs flex items-center gap-1">
                                        <Star className="w-3 h-3" />
                                        Active
                                      </span>
                                    )}
                                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                                      pack.status === 'active' 
                                        ? 'bg-emerald-500/10 text-emerald-400' 
                                        : pack.status === 'archived'
                                        ? 'bg-slate-500/20 text-slate-400'
                                        : 'bg-amber-500/10 text-amber-400'
                                    }`}>
                                      {pack.status}
                                    </span>
                                  </div>
                                  <p className="text-sm text-slate-500">
                                    v{pack.versionId} • {pack.description || 'No description'}
                                  </p>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-1 rounded-full bg-violet-500/10 text-violet-400 text-xs">
                                  {selectedTenant?.name}
                                </span>

                                <button
                                  onClick={() => setHistoryPack({ specialtyId: selectedSpecialtyId, packId: pack.packId, name: pack.name })}
                                  className="px-3 py-1.5 rounded-lg bg-slate-700/60 text-slate-300 text-sm hover:bg-slate-700 flex items-center gap-1.5"
                                  title="Version history"
                                >
                                  <Clock className="w-3.5 h-3.5" />
                                  History
                                </button>

                                {pack.status === 'draft' && (
                                  <button
                                    onClick={() => handleSetActive(pack.packId, pack.versionId)}
                                    className="px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 text-sm hover:bg-emerald-500/30"
                                  >
                                    Set Active
                                  </button>
                                )}
                                {isActive && (
                                  <button
                                    onClick={() => handleDeactivate(pack.packId, pack.versionId)}
                                    className="px-3 py-1.5 rounded-lg bg-slate-700/60 text-slate-400 text-sm hover:bg-red-500/20 hover:text-red-400"
                                  >
                                    Deactivate
                                  </button>
                                )}
                                {pack.status === 'archived' && (
                                  <button
                                    onClick={() => handleUnarchivePack(pack.packId, pack.versionId)}
                                    className="px-3 py-1.5 rounded-lg bg-sky-500/20 text-sky-400 text-sm hover:bg-sky-500/30 flex items-center gap-1.5"
                                    title="Restore from archive"
                                  >
                                    <ArchiveRestore className="w-3.5 h-3.5" />
                                    Unarchive
                                  </button>
                                )}
                                {pack.status !== 'archived' && (
                                  <button
                                    onClick={() => handleArchivePack(pack.packId, pack.versionId)}
                                    className="p-2 rounded-lg hover:bg-amber-500/20 text-slate-500 hover:text-amber-400"
                                    title="Archive"
                                  >
                                    <Archive className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleOpenEditPack(pack)}
                                  className="p-2 rounded-lg hover:bg-violet-500/20 text-slate-500 hover:text-violet-400"
                                  title="Edit pack"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeletePack(pack.packId, pack.versionId)}
                                  className="p-2 rounded-lg hover:bg-red-500/20 text-slate-500 hover:text-red-400"
                                  title={`Remove from ${selectedTenant?.name}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Empty state when no specialties enabled */}
          {totalEnabledCount === 0 && !loadingSpecialties && (
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-800/50 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-slate-600" />
              </div>
              <p className="text-slate-400 mb-2">No specialties enabled for {selectedTenant?.name}</p>
              <p className="text-sm text-slate-500">
                Expand a category above and toggle specialties to enable them for this tenant.
              </p>
            </div>
          )}
        </>
      )}

      {/* Upload / Edit Modal */}
      <Modal
        isOpen={showUpload}
        onClose={handleCloseModal}
        title={editMode ? `Edit Pack — ${editingPackName}` : `Upload Pack for ${selectedTenant?.name}`}
        size="xl"
      >
        <div className="space-y-4">

          {/* ── Edit mode banner ── */}
          {editMode && (
            <div className="p-3 bg-violet-500/10 border border-violet-500/30 rounded-lg flex items-start gap-3">
              <Pencil className="w-4 h-4 text-violet-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-violet-300">Editing existing pack</p>
                <p className="text-xs text-violet-400 mt-0.5">
                  Pack ID <code className="bg-violet-500/20 px-1 rounded">{editingPackDocId?.split('__')[0]}</code> is read-only.
                  Changes will overwrite the same document.
                </p>
              </div>
            </div>
          )}

          {/* ── Create mode: load existing shortcut ── */}
          {!editMode && packList.length > 0 && (
            <div className="border border-slate-700/50 rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-slate-800/60 border-b border-slate-700/50">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Load existing pack to edit</p>
              </div>
              <div className="divide-y divide-slate-700/30 max-h-40 overflow-y-auto">
                {packList.map(([key, pack]) => (
                  <button
                    key={key}
                    onClick={() => handleOpenEditPack(pack)}
                    className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-800/50 text-left group"
                  >
                    <div>
                      <span className="text-sm text-white group-hover:text-violet-300">{pack.name || pack.packId}</span>
                      <span className="ml-2 text-xs text-slate-500">v{pack.versionId}</span>
                    </div>
                    <Pencil className="w-3.5 h-3.5 text-slate-600 group-hover:text-violet-400" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Scope banner (create mode) ── */}
          {!editMode && (
            <div className="p-3 bg-violet-500/10 border border-violet-500/30 rounded-lg">
              <p className="text-sm text-violet-300">
                New pack will be uploaded to <strong>{selectedTenant?.name}</strong> → <strong>{enabledSpecialties[selectedSpecialtyId]?.name}</strong>
              </p>
            </div>
          )}

          {/* ── Loading spinner (edit mode while fetching pack) ── */}
          {loadingEditPack ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
              <span className="ml-2 text-slate-400 text-sm">Loading pack…</span>
            </div>
          ) : (
            <>
              {/* ── JSON editor ── */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-300">
                  {editMode ? 'Pack JSON (edit in place)' : 'Pack JSON'}
                </label>
                <textarea
                  value={jsonInput}
                  onChange={(e) => {
                    setJsonInput(e.target.value);
                    setValidation(null);
                    setParsedPack(null);
                  }}
                  placeholder='{"metadata": {"id": "pack-id", "version": "1.0.0", ...}}'
                  rows={14}
                  className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white font-mono text-sm placeholder:text-slate-600"
                />
              </div>

              <button
                onClick={handleValidate}
                disabled={!jsonInput.trim()}
                className="w-full px-4 py-2 bg-slate-700/50 hover:bg-slate-700 rounded-lg text-slate-300 disabled:opacity-50"
              >
                Validate JSON
              </button>

              {/* Validation feedback */}
              {validation && (
                <div className="space-y-3">
                  {!validation.valid && (
                    <div className="p-4 rounded-lg border bg-red-500/10 border-red-500/30">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-red-400">
                          <AlertCircle className="w-5 h-5" />
                          <span className="font-semibold">Validation errors:</span>
                        </div>
                        <ul className="list-disc list-inside text-sm text-red-300 space-y-1">
                          {validation.errors?.map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                      </div>
                    </div>
                  )}

                  {validation.valid && (
                    <>
                      <div className="p-4 rounded-lg border bg-emerald-500/10 border-emerald-500/30">
                        <div className="flex items-center gap-2 text-emerald-400">
                          <CheckCircle className="w-5 h-5" />
                          <span>Valid pack: {parsedPack?.metadata?.name} v{parsedPack?.metadata?.version}</span>
                        </div>
                      </div>

                      {validation.warnings?.length > 0 && (
                        <div className="p-4 rounded-lg border bg-amber-500/10 border-amber-500/30">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-amber-400">
                              <AlertTriangle className="w-5 h-5" />
                              <span className="font-semibold">Warnings (non-blocking):</span>
                            </div>
                            <ul className="list-disc list-inside text-sm text-amber-300 space-y-1">
                              {validation.warnings.map((w, i) => <li key={i}>{w}</li>)}
                            </ul>
                            <p className="text-xs text-amber-500 mt-2">
                              These issues have been auto-fixed with safe defaults.
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ── Redirect Flag Visual Editor (shown when pack is valid) ── */}
              {validation?.valid && parsedPack && (
                <div className="space-y-4 pt-2">

                  {/* Intra-Specialty Redirect Flags */}
                  <div className="border border-slate-700/50 rounded-xl overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 bg-amber-500/5 border-b border-slate-700/50">
                      <div>
                        <p className="text-sm font-semibold text-amber-300">Intra-Specialty Redirect Flags</p>
                        <p className="text-xs text-slate-500 mt-0.5">Redirect to another pack within the same specialty</p>
                      </div>
                      <button
                        onClick={() => { setAddingIntraRow(true); setIntraRowDraft({ ...EMPTY_REDIRECT_ROW, id: `intra_${Date.now()}` }); }}
                        disabled={addingIntraRow}
                        className="px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-300 text-xs font-medium hover:bg-amber-500/30 disabled:opacity-40 flex items-center gap-1"
                      >
                        <Plus className="w-3.5 h-3.5" /> Add Flag
                      </button>
                    </div>

                    <div className="divide-y divide-slate-700/30">
                      {(parsedPack.intraSpecialtyRedirectFlags || []).map((flag, idx) => (
                        editingIntraIdx === idx ? (
                          /* Edit inline form */
                          <div key={flag.id} className="p-4 bg-slate-800/60 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs text-slate-400 mb-1">Label *</label>
                                <input value={intraRowDraft.label || ''} onChange={e => setIntraRowDraft(p => ({...p, label: e.target.value}))}
                                  className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                              </div>
                              <div>
                                <label className="block text-xs text-slate-400 mb-1">Target Pack ID *</label>
                                <input value={intraRowDraft.targetId || ''} onChange={e => setIntraRowDraft(p => ({...p, targetId: e.target.value}))}
                                  placeholder="e.g. hematology-thrombocytopenia-v1"
                                  className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                              </div>
                              <div>
                                <label className="block text-xs text-slate-400 mb-1">Target Pack Label *</label>
                                <input value={intraRowDraft.targetLabel || ''} onChange={e => setIntraRowDraft(p => ({...p, targetLabel: e.target.value}))}
                                  placeholder="e.g. Thrombocytopenia — Hematology"
                                  className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                              </div>
                              <div>
                                <label className="block text-xs text-slate-400 mb-1">Description</label>
                                <input value={intraRowDraft.description || ''} onChange={e => setIntraRowDraft(p => ({...p, description: e.target.value}))}
                                  className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Redirect Reason *</label>
                              <input value={intraRowDraft.redirectReason || ''} onChange={e => setIntraRowDraft(p => ({...p, redirectReason: e.target.value}))}
                                placeholder="e.g. Primary issue is platelet disorder rather than anemia workup"
                                className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                            </div>
                            {(!intraRowDraft.label || !intraRowDraft.redirectReason || !intraRowDraft.targetLabel || !intraRowDraft.targetId) && (
                              <p className="text-xs text-amber-400">Label, redirect reason, target pack ID, and target label are required.</p>
                            )}
                            <div className="flex gap-2">
                              <button
                                disabled={!intraRowDraft.label || !intraRowDraft.redirectReason || !intraRowDraft.targetLabel || !intraRowDraft.targetId}
                                onClick={() => {
                                  const updated = [...(parsedPack.intraSpecialtyRedirectFlags || [])];
                                  updated[idx] = { id: flag.id, label: intraRowDraft.label, description: intraRowDraft.description || '', redirectReason: intraRowDraft.redirectReason, target: { type: 'pack', targetId: intraRowDraft.targetId, label: intraRowDraft.targetLabel } };
                                  applyRedirectChange(updated, parsedPack.crossSpecialtyRedirectFlags || []);
                                  setEditingIntraIdx(null);
                                }}
                                className="px-3 py-1.5 rounded bg-emerald-500 text-white text-xs font-medium disabled:opacity-40"
                              >Save</button>
                              <button onClick={() => setEditingIntraIdx(null)} className="px-3 py-1.5 rounded bg-slate-700 text-slate-300 text-xs">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          /* Read row */
                          <div key={flag.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-800/30">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white font-medium truncate">{flag.label}</p>
                              <p className="text-xs text-slate-500 truncate">→ {flag.target?.label || flag.target?.targetId}</p>
                            </div>
                            <div className="flex gap-1 ml-3 shrink-0">
                              <button onClick={() => { setEditingIntraIdx(idx); setIntraRowDraft({ label: flag.label, description: flag.description, redirectReason: flag.redirectReason, targetId: flag.target?.targetId, targetLabel: flag.target?.label }); }}
                                className="p-1.5 rounded hover:bg-violet-500/20 text-slate-500 hover:text-violet-400"><Pencil className="w-3.5 h-3.5" /></button>
                              <button onClick={() => { const updated = (parsedPack.intraSpecialtyRedirectFlags || []).filter((_, i) => i !== idx); applyRedirectChange(updated, parsedPack.crossSpecialtyRedirectFlags || []); }}
                                className="p-1.5 rounded hover:bg-red-500/20 text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          </div>
                        )
                      ))}

                      {/* Add intra row inline form */}
                      {addingIntraRow && (
                        <div className="p-4 bg-slate-800/60 space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Label *</label>
                              <input value={intraRowDraft.label || ''} onChange={e => setIntraRowDraft(p => ({...p, label: e.target.value}))}
                                className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Target Pack ID *</label>
                              <input value={intraRowDraft.targetId || ''} onChange={e => setIntraRowDraft(p => ({...p, targetId: e.target.value}))}
                                placeholder="e.g. hematology-thrombocytopenia-v1"
                                className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Target Pack Label *</label>
                              <input value={intraRowDraft.targetLabel || ''} onChange={e => setIntraRowDraft(p => ({...p, targetLabel: e.target.value}))}
                                placeholder="e.g. Thrombocytopenia — Hematology"
                                className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Description</label>
                              <input value={intraRowDraft.description || ''} onChange={e => setIntraRowDraft(p => ({...p, description: e.target.value}))}
                                className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs text-slate-400 mb-1">Redirect Reason *</label>
                            <input value={intraRowDraft.redirectReason || ''} onChange={e => setIntraRowDraft(p => ({...p, redirectReason: e.target.value}))}
                              placeholder="e.g. Primary issue is platelet disorder rather than anemia workup"
                              className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                          </div>
                          {(!intraRowDraft.label || !intraRowDraft.redirectReason || !intraRowDraft.targetLabel || !intraRowDraft.targetId) && (
                            <p className="text-xs text-amber-400">Label, redirect reason, target pack ID, and target label are required.</p>
                          )}
                          <div className="flex gap-2">
                            <button
                              disabled={!intraRowDraft.label || !intraRowDraft.redirectReason || !intraRowDraft.targetLabel || !intraRowDraft.targetId}
                              onClick={() => {
                                const newFlag = { id: intraRowDraft.id || `intra_${Date.now()}`, label: intraRowDraft.label, description: intraRowDraft.description || '', redirectReason: intraRowDraft.redirectReason, target: { type: 'pack', targetId: intraRowDraft.targetId, label: intraRowDraft.targetLabel } };
                                applyRedirectChange([...(parsedPack.intraSpecialtyRedirectFlags || []), newFlag], parsedPack.crossSpecialtyRedirectFlags || []);
                                setAddingIntraRow(false); setIntraRowDraft({});
                              }}
                              className="px-3 py-1.5 rounded bg-emerald-500 text-white text-xs font-medium disabled:opacity-40"
                            >Add</button>
                            <button onClick={() => { setAddingIntraRow(false); setIntraRowDraft({}); }} className="px-3 py-1.5 rounded bg-slate-700 text-slate-300 text-xs">Cancel</button>
                          </div>
                        </div>
                      )}

                      {!addingIntraRow && (parsedPack.intraSpecialtyRedirectFlags || []).length === 0 && (
                        <p className="px-4 py-3 text-xs text-slate-600 italic">No intra-specialty redirect flags defined.</p>
                      )}
                    </div>
                  </div>

                  {/* Cross-Specialty Redirect Flags */}
                  <div className="border border-slate-700/50 rounded-xl overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 bg-violet-500/5 border-b border-slate-700/50">
                      <div>
                        <p className="text-sm font-semibold text-violet-300">Cross-Specialty Redirect Flags</p>
                        <p className="text-xs text-slate-500 mt-0.5">Redirect to a different specialty entirely</p>
                      </div>
                      <button
                        onClick={() => { setAddingCrossRow(true); setCrossRowDraft({ ...EMPTY_REDIRECT_ROW, id: `cross_${Date.now()}` }); }}
                        disabled={addingCrossRow}
                        className="px-3 py-1.5 rounded-lg bg-violet-500/20 text-violet-300 text-xs font-medium hover:bg-violet-500/30 disabled:opacity-40 flex items-center gap-1"
                      >
                        <Plus className="w-3.5 h-3.5" /> Add Flag
                      </button>
                    </div>

                    <div className="divide-y divide-slate-700/30">
                      {(parsedPack.crossSpecialtyRedirectFlags || []).map((flag, idx) => (
                        editingCrossIdx === idx ? (
                          /* Edit inline form */
                          <div key={flag.id} className="p-4 bg-slate-800/60 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs text-slate-400 mb-1">Label *</label>
                                <input value={crossRowDraft.label || ''} onChange={e => setCrossRowDraft(p => ({...p, label: e.target.value}))}
                                  className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                              </div>
                              <div>
                                <label className="block text-xs text-slate-400 mb-1">Target Specialty ID *</label>
                                <input value={crossRowDraft.targetId || ''} onChange={e => setCrossRowDraft(p => ({...p, targetId: e.target.value}))}
                                  placeholder="e.g. gastroenterology"
                                  className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                              </div>
                              <div>
                                <label className="block text-xs text-slate-400 mb-1">Target Specialty Label *</label>
                                <input value={crossRowDraft.targetLabel || ''} onChange={e => setCrossRowDraft(p => ({...p, targetLabel: e.target.value}))}
                                  placeholder="e.g. Gastroenterology"
                                  className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                              </div>
                              <div>
                                <label className="block text-xs text-slate-400 mb-1">Description</label>
                                <input value={crossRowDraft.description || ''} onChange={e => setCrossRowDraft(p => ({...p, description: e.target.value}))}
                                  className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Redirect Reason *</label>
                              <input value={crossRowDraft.redirectReason || ''} onChange={e => setCrossRowDraft(p => ({...p, redirectReason: e.target.value}))}
                                placeholder="e.g. Primary issue is GI blood-loss source workup rather than hematology"
                                className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                            </div>
                            {(!crossRowDraft.label || !crossRowDraft.redirectReason || !crossRowDraft.targetLabel || !crossRowDraft.targetId) && (
                              <p className="text-xs text-amber-400">Label, redirect reason, target specialty ID, and target label are required.</p>
                            )}
                            <div className="flex gap-2">
                              <button
                                disabled={!crossRowDraft.label || !crossRowDraft.redirectReason || !crossRowDraft.targetLabel || !crossRowDraft.targetId}
                                onClick={() => {
                                  const updated = [...(parsedPack.crossSpecialtyRedirectFlags || [])];
                                  updated[idx] = { id: flag.id, label: crossRowDraft.label, description: crossRowDraft.description || '', redirectReason: crossRowDraft.redirectReason, target: { type: 'specialty', targetId: crossRowDraft.targetId, label: crossRowDraft.targetLabel } };
                                  applyRedirectChange(parsedPack.intraSpecialtyRedirectFlags || [], updated);
                                  setEditingCrossIdx(null);
                                }}
                                className="px-3 py-1.5 rounded bg-emerald-500 text-white text-xs font-medium disabled:opacity-40"
                              >Save</button>
                              <button onClick={() => setEditingCrossIdx(null)} className="px-3 py-1.5 rounded bg-slate-700 text-slate-300 text-xs">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          /* Read row */
                          <div key={flag.id} className="flex items-center justify-between px-4 py-3 hover:bg-slate-800/30">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white font-medium truncate">{flag.label}</p>
                              <p className="text-xs text-slate-500 truncate">→ {flag.target?.label || flag.target?.targetId}</p>
                            </div>
                            <div className="flex gap-1 ml-3 shrink-0">
                              <button onClick={() => { setEditingCrossIdx(idx); setCrossRowDraft({ label: flag.label, description: flag.description, redirectReason: flag.redirectReason, targetId: flag.target?.targetId, targetLabel: flag.target?.label }); }}
                                className="p-1.5 rounded hover:bg-violet-500/20 text-slate-500 hover:text-violet-400"><Pencil className="w-3.5 h-3.5" /></button>
                              <button onClick={() => { const updated = (parsedPack.crossSpecialtyRedirectFlags || []).filter((_, i) => i !== idx); applyRedirectChange(parsedPack.intraSpecialtyRedirectFlags || [], updated); }}
                                className="p-1.5 rounded hover:bg-red-500/20 text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          </div>
                        )
                      ))}

                      {/* Add cross row inline form */}
                      {addingCrossRow && (
                        <div className="p-4 bg-slate-800/60 space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Label *</label>
                              <input value={crossRowDraft.label || ''} onChange={e => setCrossRowDraft(p => ({...p, label: e.target.value}))}
                                className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Target Specialty ID *</label>
                              <input value={crossRowDraft.targetId || ''} onChange={e => setCrossRowDraft(p => ({...p, targetId: e.target.value}))}
                                placeholder="e.g. gastroenterology"
                                className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Target Specialty Label *</label>
                              <input value={crossRowDraft.targetLabel || ''} onChange={e => setCrossRowDraft(p => ({...p, targetLabel: e.target.value}))}
                                placeholder="e.g. Gastroenterology"
                                className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Description</label>
                              <input value={crossRowDraft.description || ''} onChange={e => setCrossRowDraft(p => ({...p, description: e.target.value}))}
                                className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs text-slate-400 mb-1">Redirect Reason *</label>
                            <input value={crossRowDraft.redirectReason || ''} onChange={e => setCrossRowDraft(p => ({...p, redirectReason: e.target.value}))}
                              placeholder="e.g. Primary issue is GI blood-loss source workup rather than hematology"
                              className="w-full bg-slate-700/50 border border-slate-600/50 rounded px-3 py-1.5 text-white text-sm" />
                          </div>
                          {(!crossRowDraft.label || !crossRowDraft.redirectReason || !crossRowDraft.targetLabel || !crossRowDraft.targetId) && (
                            <p className="text-xs text-amber-400">Label, redirect reason, target specialty ID, and target label are required.</p>
                          )}
                          <div className="flex gap-2">
                            <button
                              disabled={!crossRowDraft.label || !crossRowDraft.redirectReason || !crossRowDraft.targetLabel || !crossRowDraft.targetId}
                              onClick={() => {
                                const newFlag = { id: crossRowDraft.id || `cross_${Date.now()}`, label: crossRowDraft.label, description: crossRowDraft.description || '', redirectReason: crossRowDraft.redirectReason, target: { type: 'specialty', targetId: crossRowDraft.targetId, label: crossRowDraft.targetLabel } };
                                applyRedirectChange(parsedPack.intraSpecialtyRedirectFlags || [], [...(parsedPack.crossSpecialtyRedirectFlags || []), newFlag]);
                                setAddingCrossRow(false); setCrossRowDraft({});
                              }}
                              className="px-3 py-1.5 rounded bg-emerald-500 text-white text-xs font-medium disabled:opacity-40"
                            >Add</button>
                            <button onClick={() => { setAddingCrossRow(false); setCrossRowDraft({}); }} className="px-3 py-1.5 rounded bg-slate-700 text-slate-300 text-xs">Cancel</button>
                          </div>
                        </div>
                      )}

                      {!addingCrossRow && (parsedPack.crossSpecialtyRedirectFlags || []).length === 0 && (
                        <p className="px-4 py-3 text-xs text-slate-600 italic">No cross-specialty redirect flags defined.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-3 rounded-lg bg-slate-800/50 text-slate-300 hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  onClick={editMode ? handleEditSave : handleUploadSave}
                  disabled={!validation?.valid || saving}
                  className={`flex-1 px-4 py-3 rounded-lg text-white disabled:opacity-50 flex items-center justify-center gap-2 ${
                    editMode
                      ? 'bg-violet-600 hover:bg-violet-700'
                      : 'bg-emerald-500 hover:bg-emerald-600'
                  }`}
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : editMode ? (
                    <Pencil className="w-4 h-4" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  {editMode ? 'Save Changes' : `Upload to ${selectedTenant?.name}`}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {historyPack && (
        <PackVersionHistoryDrawer
          tenantId={selectedTenantId}
          specialtyId={historyPack.specialtyId}
          packId={historyPack.packId}
          packName={historyPack.name}
          onClose={() => setHistoryPack(null)}
        />
      )}
    </div>
  );
};

export default PackManagerView;
